/**
 * Tiny synthesised sound engine — no asset files, everything is oscillators.
 * Sounds are described declaratively so games can add their own without
 * touching WebAudio directly.
 */

export interface ToneSpec {
  type?: OscillatorType;
  /** Frequency in Hz, or [from, to] to sweep. */
  freq: number | [number, number];
  /** Seconds. */
  duration: number;
  /** Delay before this tone starts, in seconds. */
  at?: number;
  gain?: number;
}

export type SfxSpec = ToneSpec[];

/** Note names to Hz, A4 = 440. Accepts "C#4", "Eb3", or "-" for a rest. */
export function noteToFreq(note: string): number {
  if (note === "-" || note === "") return 0;
  const match = /^([A-Ga-g])([#b]?)(-?\d)$/.exec(note);
  if (!match) return 0;
  const base: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
  const semitone = base[match[1].toUpperCase()] + (match[2] === "#" ? 1 : match[2] === "b" ? -1 : 0);
  const octave = Number(match[3]);
  return 440 * Math.pow(2, (semitone - 9) / 12 + (octave - 4));
}

export interface TrackSpec {
  /** Beats per minute. */
  tempo: number;
  type?: OscillatorType;
  gain?: number;
  /** Note names; each entry lasts one step. Use "-" to rest, "." to sustain. */
  notes: string[];
  /** Steps per beat. */
  subdivision?: number;
}

export interface SongSpec {
  tracks: TrackSpec[];
}

const SFX: Record<string, SfxSpec> = {
  cursor: [{ type: "square", freq: 880, duration: 0.04, gain: 0.14 }],
  confirm: [
    { type: "square", freq: 660, duration: 0.05, gain: 0.16 },
    { type: "square", freq: 990, duration: 0.07, at: 0.05, gain: 0.16 },
  ],
  cancel: [{ type: "square", freq: 300, duration: 0.08, gain: 0.14 }],
  text: [{ type: "square", freq: 1500, duration: 0.012, gain: 0.05 }],
  step: [{ type: "triangle", freq: 150, duration: 0.05, gain: 0.09 }],
  bump: [{ type: "square", freq: [180, 90], duration: 0.09, gain: 0.14 }],
  attack: [{ type: "sawtooth", freq: [900, 200], duration: 0.14, gain: 0.18 }],
  hit: [{ type: "square", freq: [420, 90], duration: 0.2, gain: 0.2 }],
  miss: [{ type: "sine", freq: [500, 700], duration: 0.12, gain: 0.12 }],
  hurt: [{ type: "sawtooth", freq: [260, 70], duration: 0.28, gain: 0.2 }],
  spell: [
    { type: "sine", freq: [400, 1600], duration: 0.22, gain: 0.16 },
    { type: "square", freq: [1600, 400], duration: 0.2, at: 0.18, gain: 0.12 },
  ],
  heal: [
    { type: "sine", freq: 660, duration: 0.1, gain: 0.14 },
    { type: "sine", freq: 880, duration: 0.1, at: 0.09, gain: 0.14 },
    { type: "sine", freq: 1320, duration: 0.18, at: 0.18, gain: 0.14 },
  ],
  defeat: [{ type: "square", freq: [400, 40], duration: 0.5, gain: 0.18 }],
  encounter: [
    { type: "square", freq: [200, 900], duration: 0.18, gain: 0.2 },
    { type: "square", freq: [200, 900], duration: 0.18, at: 0.18, gain: 0.2 },
    { type: "square", freq: [200, 1200], duration: 0.3, at: 0.36, gain: 0.22 },
  ],
  victory: [
    { type: "square", freq: 523, duration: 0.1, gain: 0.16 },
    { type: "square", freq: 659, duration: 0.1, at: 0.1, gain: 0.16 },
    { type: "square", freq: 784, duration: 0.1, at: 0.2, gain: 0.16 },
    { type: "square", freq: 1047, duration: 0.34, at: 0.3, gain: 0.18 },
  ],
  levelup: [
    { type: "square", freq: 784, duration: 0.08, gain: 0.16 },
    { type: "square", freq: 988, duration: 0.08, at: 0.08, gain: 0.16 },
    { type: "square", freq: 1175, duration: 0.08, at: 0.16, gain: 0.16 },
    { type: "square", freq: 1568, duration: 0.4, at: 0.24, gain: 0.18 },
  ],
  gameover: [
    { type: "triangle", freq: 300, duration: 0.3, gain: 0.18 },
    { type: "triangle", freq: 240, duration: 0.3, at: 0.3, gain: 0.18 },
    { type: "triangle", freq: 180, duration: 0.8, at: 0.6, gain: 0.18 },
  ],
  chest: [
    { type: "square", freq: 1047, duration: 0.07, gain: 0.15 },
    { type: "square", freq: 1319, duration: 0.07, at: 0.07, gain: 0.15 },
    { type: "square", freq: 1568, duration: 0.25, at: 0.14, gain: 0.15 },
  ],
};

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private musicTimer = 0;
  private currentSong: SongSpec | null = null;
  private muted = false;

  /** WebAudio needs a user gesture; call this from any first interaction. */
  unlock(): void {
    this.ensureContext();
    void this.ctx?.resume();
  }

  get isMuted(): boolean {
    return this.muted;
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    if (this.master) this.master.gain.value = this.muted ? 0 : 0.6;
    return this.muted;
  }

  private ensureContext(): AudioContext | null {
    if (this.ctx) return this.ctx;
    const Ctor = window.AudioContext ?? (window as never as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    this.ctx = new Ctor();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.muted ? 0 : 0.6;
    this.master.connect(this.ctx.destination);
    this.musicGain = this.ctx.createGain();
    this.musicGain.gain.value = 0.35;
    this.musicGain.connect(this.master);
    return this.ctx;
  }

  /** Plays a named preset, or a custom spec. */
  play(name: keyof typeof SFX | SfxSpec): void {
    const spec = typeof name === "string" ? SFX[name] : name;
    if (!spec) return;
    const ctx = this.ensureContext();
    if (!ctx || !this.master) return;
    if (ctx.state === "suspended") void ctx.resume();

    const now = ctx.currentTime;
    for (const tone of spec) this.scheduleTone(tone, now + (tone.at ?? 0), this.master);
  }

  private scheduleTone(tone: ToneSpec, start: number, destination: AudioNode): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = tone.type ?? "square";

    if (Array.isArray(tone.freq)) {
      osc.frequency.setValueAtTime(tone.freq[0], start);
      osc.frequency.exponentialRampToValueAtTime(Math.max(1, tone.freq[1]), start + tone.duration);
    } else {
      osc.frequency.setValueAtTime(tone.freq, start);
    }

    // Short attack, exponential decay: the shape of every 8-bit blip.
    const peak = tone.gain ?? 0.15;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(peak, start + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + tone.duration);

    osc.connect(gain).connect(destination);
    osc.start(start);
    osc.stop(start + tone.duration + 0.02);
  }

  // ----------------------------------------------------------------- music

  /**
   * Loops a song. Scheduling happens one bar at a time from `updateMusic`,
   * which keeps the note queue short enough to stop instantly.
   */
  playSong(song: SongSpec): void {
    if (this.currentSong === song) return;
    this.stopSong();
    this.ensureContext();
    this.currentSong = song;
    this.musicTimer = 0;
  }

  stopSong(): void {
    this.currentSong = null;
    this.musicTimer = 0;
  }

  /** Drive from the main loop. */
  update(dt: number): void {
    if (!this.currentSong || !this.ctx || !this.musicGain) return;
    if (this.ctx.state !== "running") return;

    this.musicTimer -= dt;
    if (this.musicTimer > 0) return;

    let longest = 0;
    for (const track of this.currentSong.tracks) {
      const stepSeconds = 60 / track.tempo / (track.subdivision ?? 2);
      const start = this.ctx.currentTime + 0.05;
      let i = 0;
      while (i < track.notes.length) {
        const note = track.notes[i];
        // A run of "." extends the previous note rather than retriggering it.
        let length = 1;
        while (track.notes[i + length] === ".") length++;
        const freq = noteToFreq(note);
        if (freq > 0) {
          this.scheduleTone(
            {
              type: track.type ?? "square",
              freq,
              duration: stepSeconds * length * 0.92,
              gain: track.gain ?? 0.1,
            },
            start + i * stepSeconds,
            this.musicGain,
          );
        }
        i += length;
      }
      longest = Math.max(longest, track.notes.length * stepSeconds);
    }
    this.musicTimer = longest;
  }
}
